import { FormEvent, useEffect, useMemo, useState } from 'react';
import { Button, IconButton, InputAdornment, TextField } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { NavLink, useNavigate, useSearchParams } from 'react-router-dom';
import ROUTES from '../../common/Routing/Routes';
import { useAuth } from '../../common/Auth/AuthProvider';
import UsersService from '../../common/Rest/Services/UserService/UsersService';
import { AuthConnectionType } from '../../common/Rest/Services/UserService/Data';
import { ValidateConfirmPassword, ValidateEmail, ValidatePassword } from '../../common/Utils/Utils';
import './PasswordRecoveryPage.css';

type RecoveryStep = 'request' | 'token' | 'password' | 'success';

const extractToken = (value?: string | null) => {
  const trimmed = value?.trim();
  if (!trimmed) return '';

  try {
    const url = new URL(trimmed);
    return url.searchParams.get('code') || url.searchParams.get('token') || '';
  } catch {
    const queryIndex = trimmed.indexOf('?');
    if (queryIndex >= 0) {
      const params = new URLSearchParams(trimmed.slice(queryIndex + 1));
      return params.get('code') || params.get('token') || '';
    }
  }

  return trimmed;
};

function PasswordRecoveryPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const initialToken = useMemo(
    () => extractToken(searchParams.get('code') || searchParams.get('token')),
    [searchParams]
  );

  const [step, setStep] = useState<RecoveryStep>(initialToken ? 'token' : 'request');
  const [email, setEmail] = useState('');
  const [tokenInput, setTokenInput] = useState(initialToken);
  const [token, setToken] = useState(initialToken);
  const [maskedEmail, setMaskedEmail] = useState<string | null>(null);
  const [pendingAuthProvider, setPendingAuthProvider] = useState<AuthConnectionType | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState('Пароль змінено. Тепер можна увійти з новим паролем.');
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{ [key: string]: string }>({});
  const [submitting, setSubmitting] = useState(false);
  const [validating, setValidating] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const validateToken = async (rawToken: string) => {
    const normalizedToken = extractToken(rawToken);
    if (!normalizedToken) {
      setError('Вставте код або посилання з листа.');
      setStep('token');
      return;
    }

    try {
      setValidating(true);
      setError(null);
      const result = await UsersService.validatePasswordRecoveryToken({ token: normalizedToken });
      if (!result.isValid) {
        setToken('');
        setPendingAuthProvider(null);
        setStep('token');
        setError('Посилання недійсне або строк його дії минув.');
        return;
      }

      setToken(normalizedToken);
      setTokenInput(normalizedToken);
      setMaskedEmail(result.maskedEmail || null);
      setPendingAuthProvider(result.pendingAuthProvider || null);
      setStep('password');
    } catch {
      setToken('');
      setPendingAuthProvider(null);
      setStep('token');
      setError('Не вдалося перевірити посилання. Спробуйте ще раз.');
    } finally {
      setValidating(false);
    }
  };

  useEffect(() => {
    if (initialToken) {
      validateToken(initialToken);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialToken]);

  const handleRequestSubmit = async (event: FormEvent) => {
    event.preventDefault();
    const validation = ValidateEmail(email);
    if (!validation.isValid) {
      setFieldErrors({ email: validation.validationMessage || '' });
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      setFieldErrors({});
      const result = await UsersService.requestPasswordRecovery({ email: email.trim() });
      setMessage(result.message);
      setStep('token');
    } catch {
      setMessage('Якщо акаунт із такою поштою існує, ми надіслали інструкцію для відновлення пароля.');
      setStep('token');
    } finally {
      setSubmitting(false);
    }
  };

  const handleTokenSubmit = async (event: FormEvent) => {
    event.preventDefault();
    await validateToken(tokenInput);
  };

  const handlePasswordSubmit = async (event: FormEvent) => {
    event.preventDefault();
    const passwordValidation = ValidatePassword(newPassword);
    const confirmValidation = ValidateConfirmPassword(newPassword, confirmPassword);
    const nextErrors: { [key: string]: string } = {};

    if (!passwordValidation.isValid) nextErrors.newPassword = passwordValidation.validationMessage || '';
    if (!confirmValidation.isValid) nextErrors.confirmPassword = confirmValidation.validationMessage || '';

    setFieldErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    try {
      setSubmitting(true);
      setError(null);
      const result = await UsersService.confirmPasswordRecovery({
        token,
        newPassword,
      });

      if (result.auth?.token) {
        login(result.auth.token);
        navigate(ROUTES.PROFILE, { replace: true });
        return;
      }

      setSuccessMessage(
        result.linkedAuthProvider === 'Google'
          ? 'Пароль змінено, Google прив’язано до акаунта. Тепер можна входити через Google.'
          : 'Пароль змінено. Тепер можна увійти з новим паролем.'
      );
      setStep('success');
    } catch {
      setError('Не вдалося змінити пароль. Спробуйте запросити нове посилання.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="auth-page default-container">
      <div className="password-recovery-page">
        <div className="password-recovery-heading">
          <h1>Відновлення пароля</h1>
          <p>Вкажіть email або відкрийте посилання з листа, щоб створити новий пароль.</p>
        </div>

        {step === 'request' && (
          <form className="login-page" onSubmit={handleRequestSubmit} noValidate>
            <TextField
              id="password-recovery-email"
              label="Email"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              error={!!fieldErrors.email}
              helperText={fieldErrors.email}
              fullWidth
            />

            <Button variant="contained" type="submit" disabled={submitting} fullWidth>
              {submitting ? 'Надсилаємо...' : 'Надіслати інструкцію'}
            </Button>

            <Button variant="text" type="button" onClick={() => setStep('token')} fullWidth>
              Вставити код з листа
            </Button>
          </form>
        )}

        {step === 'token' && (
          <form className="login-page" onSubmit={handleTokenSubmit} noValidate>
            {message && <p className="password-recovery-message">{message}</p>}

            <TextField
              id="password-recovery-token"
              label="Код або посилання з листа"
              value={tokenInput}
              onChange={(event) => setTokenInput(event.target.value)}
              fullWidth
              multiline
              minRows={2}
            />

            {error && <div className="error-message">{error}</div>}

            <Button variant="contained" type="submit" disabled={validating} fullWidth>
              {validating ? 'Перевіряємо...' : 'Продовжити'}
            </Button>

            <Button variant="text" type="button" onClick={() => setStep('request')} fullWidth>
              Надіслати нове посилання
            </Button>
          </form>
        )}

        {step === 'password' && (
          <form className="login-page" onSubmit={handlePasswordSubmit} noValidate>
            {maskedEmail && (
              <p className="password-recovery-message">
                Посилання підтверджено для {maskedEmail}.
              </p>
            )}
            {pendingAuthProvider === 'Google' && (
              <p className="password-recovery-message">
                Після зміни пароля Google буде прив'язано до цього акаунта.
              </p>
            )}

            <TextField
              id="password-recovery-new-password"
              label="Новий пароль"
              type={showPassword ? 'text' : 'password'}
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
              error={!!fieldErrors.newPassword}
              helperText={fieldErrors.newPassword}
              fullWidth
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label={showPassword ? 'Приховати пароль' : 'Показати пароль'}
                        onClick={() => setShowPassword((prev) => !prev)}
                        edge="end"
                        tabIndex={-1}
                      >
                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />

            <TextField
              id="password-recovery-confirm-password"
              label="Повторіть пароль"
              type={showConfirmPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
              error={!!fieldErrors.confirmPassword}
              helperText={fieldErrors.confirmPassword}
              fullWidth
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label={showConfirmPassword ? 'Приховати пароль' : 'Показати пароль'}
                        onClick={() => setShowConfirmPassword((prev) => !prev)}
                        edge="end"
                        tabIndex={-1}
                      >
                        {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />

            {error && <div className="error-message">{error}</div>}

            <Button variant="contained" type="submit" disabled={submitting} fullWidth>
              {submitting
                ? 'Змінюємо...'
                : pendingAuthProvider === 'Google'
                  ? "Змінити пароль і прив'язати Google"
                  : 'Змінити пароль'}
            </Button>
          </form>
        )}

        {step === 'success' && (
          <>
            <p className="password-recovery-message">{successMessage}</p>
            <div className="password-recovery-actions">
              <NavLink to={ROUTES.LOGIN}>Перейти до входу</NavLink>
              <Button variant="contained" onClick={() => navigate(ROUTES.LOGIN)}>
                Увійти
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default PasswordRecoveryPage;
